import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { DISHES, type DishKind } from "../game/dishes";
import { describeBuild, friesStatus, pattyDoneness, pattyStatus } from "../game/food";
import { EYE, tubSlot, type Aim, type Layout, type Pose, type Station } from "../game/kitchen";
import { machineStatus, setupKinds, type Dish, type ShiftState } from "../game/shift";
import type { Room } from "./build";
import { makeDish, makeRack, poseDish, rackLift, rackSlot, type DishModel } from "./dishes";
import { ContentsModel, FriesModel, PattyModel } from "./food";
import { lookFor } from "./looks";
import { CookMarker } from "./markers";
import { VIEW_FAR } from "./boardwalk";
import { PersonModel } from "./people";
import { CupFill } from "./drinks";
import { PaperCupModel, PitcherModel, PortafilterModel, ShotModel } from "./coffee";
import { buildTruck } from "./truck";
import { heatStatus } from "../game/coffee";
import { customerPose } from "../game/customers";
import { buildRoom } from "./room";
import { buildShack } from "./shack";
import { ticketTexture } from "./textures";

// The kitchen in three.js, drawn from the shift state and your pose each
// frame. It owns no game logic: `sync` puts every dish where the state says
// it is (tub, your hands, sink, rack, machine), stacks the shelf to its
// counts, cooks the patties and fries you can see, builds the order on the
// board, hangs the tickets, and moves the machine's hood and the clock.

/** What the engine tells the scene besides the state: how you're moving and what you just did. */
export interface Frame {
  pose: Pose;
  aim: Aim;
  /** Seconds since the page started, for animation. */
  time: number;
  /** How fast you're walking, 0–1, for the head bob. */
  walking: number;
  /** When you last scrubbed (`time`), for the sponge swipe and the suds. */
  scrubbedAt: number;
  /** When a dish last came clean, for the sparkle over the rack. */
  cleanAt: number;
  /** When something last jolted the view: the hood slamming, a crash. */
  joltAt: number;
  /** When an order last went out the window, for the bell. */
  rungAt: number;
}

const HOOD_LIFT = 0.48;

const SHELF_MAX = 12;

export class KitchenScene {
  readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(70, 1, 0.03, 40);
  private readonly room: Room;
  private readonly dishes = new Map<number, DishModel>();
  private readonly drainRack = makeRack();
  private readonly machineRack = makeRack();
  private readonly heldRack = makeRack();
  private readonly hands = new THREE.Group();
  private readonly gloveL: THREE.Object3D;
  private readonly gloveR: THREE.Object3D;
  private readonly sponge: THREE.Mesh;
  private readonly shelfStacks: Record<DishKind, DishModel[]>;
  private readonly tickets = new Map<number, THREE.Mesh>();
  private readonly steam: THREE.Mesh[] = [];
  private readonly bubbles: THREE.Mesh[] = [];
  private readonly sparkles: THREE.Mesh[] = [];
  /** Where the newest clean dish landed, for the sparkle. */
  private readonly sparkleAt = new THREE.Vector3();
  private lastRackCount = 0;
  private hoodOpen = 1;
  /** How far apart the hands sit: narrower on a portrait phone, so they stay in view. */
  private handSpread = 1;
  private lastTime = 0;
  /** Cooking you can see: a patty per flat-top spot, fries per fryer basket, the order on the board, and food in hand. */
  private readonly griddlePatties: PattyModel[] = [];
  private readonly fryerFries: FriesModel[] = [];
  /** Over each flat-top spot, then each fryer basket: what the food there needs. */
  private readonly griddleMarkers: CookMarker[] = [];
  private readonly fryerMarkers: CookMarker[] = [];
  /** The customers out front, by order number. */
  private readonly people = new Map<number, PersonModel>();
  private readonly spots = new Map<number, number>();
  /** The drink in each soda cup, by the cup's dish id. */
  private readonly fills = new Map<number, CupFill>();
  private readonly boardContents: ContentsModel[] = [];
  private readonly heldContents = new ContentsModel();
  private readonly heldPatty = new PattyModel();
  private readonly heldFries = new FriesModel();
  private readonly smoke: THREE.Mesh[] = [];
  /** The coffee truck's: a shot glass and a locked-in portafilter per group head, the pitcher on the wand, the cup on the bar, and what's in hand. */
  private readonly groupShots: ShotModel[] = [];
  private readonly groupFilters: PortafilterModel[] = [];
  private readonly groupMarkers: CookMarker[] = [];
  private readonly wandPitcher = new PitcherModel();
  private readonly wandMarker = new CookMarker();
  private readonly barCup = new PaperCupModel();
  private readonly heldFilter = new PortafilterModel();
  private readonly heldShot = new ShotModel();
  private readonly heldPitcher = new PitcherModel();
  private readonly heldCoffee = new PaperCupModel();

  constructor(
    canvas: HTMLCanvasElement,
    restaurant: { id: string; name: string },
    private readonly layout: Layout,
  ) {
    const look = lookFor(restaurant.id);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.45;
    pmrem.dispose();
    this.scene.background = new THREE.Color(0x222222);

    this.scene.add(new THREE.HemisphereLight(0xfff8ec, 0x6b4a3a, 0.9));
    this.room =
      layout.id === "burger-shack" ? buildShack(restaurant.name, look) : layout.id === "coffee-truck" ? buildTruck(restaurant.name, look) : buildRoom(restaurant.name, look);
    const key = new THREE.DirectionalLight(look.light, look.lightIntensity);
    key.position.copy(this.room.keyLight.from);
    key.target.position.copy(this.room.keyLight.at);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    const sc = key.shadow.camera;
    sc.left = sc.bottom = -4;
    sc.right = sc.top = 4;
    sc.near = 0.5;
    sc.far = 8;
    key.shadow.radius = 3;
    key.shadow.bias = -0.0005;
    key.shadow.normalBias = 0.02;
    this.scene.add(key, key.target);
    const pass = new THREE.PointLight(look.glow, 1.2, 4, 1.5);
    pass.position.copy(this.room.passLight);
    this.scene.add(pass);

    this.scene.add(this.room.group);
    if (this.room.outside) {
      this.scene.fog = this.room.outside.fog;
      this.camera.far = VIEW_FAR;
      this.camera.updateProjectionMatrix();
    }
    const cooking = this.room.cooking;
    if (cooking) {
      for (const at of cooking.griddle) {
        const p = new PattyModel();
        p.mesh.position.copy(at).setY(at.y + 0.009);
        this.griddlePatties.push(p);
        this.scene.add(p.mesh);
        const m = new CookMarker();
        m.sprite.position.copy(at).setY(at.y + 0.17);
        this.griddleMarkers.push(m);
        this.scene.add(m.sprite);
      }
      for (const at of cooking.fryer) {
        const f = new FriesModel();
        f.group.position.copy(at).setY(at.y - 0.01);
        this.fryerFries.push(f);
        this.scene.add(f.group);
        const m = new CookMarker();
        m.sprite.position.copy(at).setY(at.y + 0.24);
        this.fryerMarkers.push(m);
        this.scene.add(m.sprite);
      }
      for (const at of cooking.board) {
        const c = new ContentsModel();
        c.group.position.copy(at).setY(at.y + 0.008);
        this.boardContents.push(c);
        this.scene.add(c.group);
      }
    }
    this.heldPatty.mesh.position.set(0, -0.2, -0.42);
    this.heldFries.group.position.set(0, -0.21, -0.42);
    this.heldContents.group.position.set(0, -0.22, -0.46);
    this.hands.add(this.heldPatty.mesh, this.heldFries.group, this.heldContents.group);
    this.drainRack.position.copy(this.room.anchors.rack);
    this.machineRack.position.copy(this.room.anchors.machineRack);
    // A truck has no dish machine or drainboard.
    this.drainRack.visible = this.machineRack.visible = !!layout.stations.rack;
    const cafe = this.room.cafe;
    if (cafe) {
      cafe.groups.forEach((at, i) => {
        const shot = new ShotModel();
        shot.group.position.copy(at);
        const pf = new PortafilterModel();
        pf.group.position.copy(cafe.grouphead[i]!).setY(cafe.grouphead[i]!.y - 0.05);
        pf.group.rotation.y = -Math.PI / 2;
        pf.set(1);
        const marker = new CookMarker();
        marker.sprite.position.copy(at).setY(1.7);
        this.groupShots.push(shot);
        this.groupFilters.push(pf);
        this.groupMarkers.push(marker);
        this.scene.add(shot.group, pf.group, marker.sprite);
      });
      this.wandPitcher.group.position.copy(cafe.wand);
      this.wandMarker.sprite.position.copy(cafe.wand).setY(1.7);
      this.barCup.group.position.copy(cafe.bar).setY(cafe.bar.y + 0.002);
      this.scene.add(this.wandPitcher.group, this.wandMarker.sprite, this.barCup.group);
      this.heldFilter.group.position.set(0.02, -0.25, -0.46);
      this.heldFilter.group.rotation.y = -Math.PI / 2;
      this.heldShot.group.position.set(0, -0.27, -0.4);
      this.heldPitcher.group.position.set(0, -0.3, -0.42);
      this.heldCoffee.group.position.set(0, -0.3, -0.42);
      this.hands.add(this.heldFilter.group, this.heldShot.group, this.heldPitcher.group, this.heldCoffee.group);
    }
    this.scene.add(this.drainRack, this.machineRack);

    // Your hands: yellow rubber gloves, and a sponge in the right one.
    const gloveMat = new THREE.MeshStandardMaterial({ color: 0xf3c623, roughness: 0.45 });
    const glove = () => {
      const g = new THREE.Group();
      const palm = new THREE.Mesh(new THREE.CapsuleGeometry(0.026, 0.05, 6, 12), gloveMat);
      palm.rotation.x = Math.PI / 2;
      palm.scale.set(1.25, 0.7, 1);
      g.add(palm);
      const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(0.009, 0.03, 4, 8), gloveMat);
      thumb.position.set(0.03, 0.005, -0.01);
      thumb.rotation.set(Math.PI / 2, 0, -0.6);
      g.add(thumb);
      const cuff = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.03, 0.07, 14), gloveMat);
      cuff.rotation.x = Math.PI / 2;
      cuff.position.z = 0.065;
      g.add(cuff);
      return g;
    };
    this.gloveL = glove();
    this.gloveR = glove();
    this.sponge = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.03, 0.045), new THREE.MeshStandardMaterial({ color: 0x7fc66b, roughness: 0.95 }));
    this.sponge.position.set(0, -0.035, -0.02);
    this.gloveR.add(this.sponge);
    this.hands.add(this.gloveL, this.gloveR, this.heldRack);
    this.camera.add(this.hands);
    this.camera.rotation.order = "YXZ";
    this.scene.add(this.camera);

    this.shelfStacks = { plate: [], mug: [], bowl: [], cup: [], sauce: [], wok: [], basket: [], tumbler: [], shotglass: [], pitcher: [] };
    const puff = new THREE.SphereGeometry(1, 8, 6);
    for (let i = 0; i < 16; i++) {
      const m = new THREE.Mesh(puff, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false }));
      this.steam.push(m);
      this.scene.add(m);
    }
    for (let i = 0; i < 12; i++) {
      const m = new THREE.Mesh(puff, new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0, roughness: 0.1 }));
      this.bubbles.push(m);
      this.scene.add(m);
    }
    for (let i = 0; i < 12; i++) {
      const m = new THREE.Mesh(puff, new THREE.MeshBasicMaterial({ color: 0x8a8a8a, transparent: true, opacity: 0, depthWrite: false }));
      this.smoke.push(m);
      this.scene.add(m);
    }
    // A little four-pointed glint, drawn additively so it glows.
    const star = new THREE.Shape();
    for (let i = 0; i < 8; i++) {
      const r = i % 2 ? 0.25 : 1;
      const a = (i / 8) * Math.PI * 2;
      if (i === 0) star.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else star.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    const starGeo = new THREE.ShapeGeometry(star);
    for (let i = 0; i < 6; i++) {
      const m = new THREE.Mesh(starGeo, new THREE.MeshBasicMaterial({ color: 0xfff6c8, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
      this.sparkles.push(m);
      this.scene.add(m);
    }
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    // A tall phone screen sees more up and down than sideways; widen it a little.
    this.camera.fov = this.camera.aspect < 1 ? 78 : 60;
    this.handSpread = Math.max(0.5, Math.min(1, this.camera.aspect / 1.3));
    this.camera.updateProjectionMatrix();
  }

  sync(s: ShiftState, f: Frame): void {
    const dt = Math.min(0.1, Math.max(0, f.time - this.lastTime));
    this.lastTime = f.time;
    const bob = Math.sin(f.time * 9) * 0.018 * f.walking;
    this.camera.position.set(f.pose.x, EYE + bob, f.pose.z);
    const jolt = f.time - f.joltAt < 0.3 ? Math.sin((f.time - f.joltAt) * 60) * 0.012 * (1 - (f.time - f.joltAt) / 0.3) : 0;
    this.camera.rotation.set(f.pose.pitch + jolt, f.pose.yaw, 0);

    this.syncDishes(s, f);
    this.syncHands(s, f, bob);
    this.syncMachine(s, f, dt);
    this.syncShelf(s);
    this.syncTickets(s);
    this.syncHighlight(f.aim);
    this.syncBubbles(f, dt);
    this.syncSparkles(s, f);
    this.syncCooking(s, f);
    this.room.outside?.animate(f.time);
    this.syncPeople(s, f);
    this.syncCafe(s, f);

    // The wall clock runs on shift time.
    const [open, close] = s.setup.hours;
    const hours = open + ((close - open) * s.t) / s.setup.shiftSeconds;
    this.room.hourHand.rotation.z = -((hours % 12) / 12) * Math.PI * 2;
    this.room.minuteHand.rotation.z = -(hours % 1) * Math.PI * 2;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /** Draw calls in the last frame, for `#debug` and performance checks. */
  drawCalls(): number {
    return this.renderer.info.render.calls;
  }

  dispose(): void {
    this.renderer.dispose();
  }

  private model(dish: Dish): DishModel {
    let m = this.dishes.get(dish.id);
    if (!m) {
      m = makeDish(dish.kind);
      this.dishes.set(dish.id, m);
      this.scene.add(m.group);
    }
    m.setGrime(dish.grime);
    return m;
  }

  private syncDishes(s: ShiftState, f: Frame): void {
    const seen = new Set<number>();
    const place = (dish: Dish, parent: THREE.Object3D, x: number, y: number, z: number, how: "upright" | "racked") => {
      const m = this.model(dish);
      if (m.group.parent !== parent) parent.add(m.group);
      m.group.position.set(x, y, z);
      poseDish(m.group, dish.kind, how, (dish.id * 0.618) % 1);
      m.body.emissive.setHex(f.aim?.dishId === dish.id ? 0x444433 : 0x000000);
      seen.add(dish.id);
      return m;
    };
    s.tub.forEach((d, i) => {
      const [x, y, z] = tubSlot(this.layout, i);
      place(d, this.scene, x, y - 0.06, z, "upright");
    });
    if (s.sink) {
      const a = this.room.anchors.sink;
      const m = place(s.sink, this.scene, a.x, a.y, a.z, "upright");
      // A wobble as the sponge goes round.
      const since = f.time - f.scrubbedAt;
      if (since < 0.25) m.group.rotation.z = Math.sin(since * 40) * 0.08 * (1 - since / 0.25);
    }
    s.rack.forEach((d, i) => {
      const [x, y, z] = rackSlot(i);
      place(d, this.drainRack, x, y + rackLift(d.kind), z, "racked");
    });
    const inMachine = s.machine?.dishes ?? [];
    inMachine.forEach((d, i) => {
      const [x, y, z] = rackSlot(i);
      place(d, this.machineRack, x, y + rackLift(d.kind), z, "racked");
    });
    this.machineRack.visible = s.machine !== null;
    if (s.held?.kind === "dish") place(s.held.dish, this.hands, 0, -0.23, -0.46, "upright");
    if (s.held?.kind === "basket") place(s.held.basket.dish, this.hands, 0, -0.23, -0.46, "upright");
    const cups = [...(s.held?.kind === "cup" ? [s.held.cup] : []), ...s.ledge];
    if (s.held?.kind === "cup") place(s.held.cup.dish, this.hands, 0, -0.27, -0.42, "upright");
    s.ledge.forEach((c, i) => {
      const at = this.room.cooking?.ledge[i];
      if (at) place(c.dish, this.scene, at.x, at.y, at.z, "upright");
    });
    for (const c of cups) {
      let fill = this.fills.get(c.dish.id);
      if (!fill) {
        fill = new CupFill();
        this.fills.set(c.dish.id, fill);
      }
      const m = this.dishes.get(c.dish.id)!;
      if (fill.group.parent !== m.group) m.group.add(fill.group);
      fill.set(c);
    }
    for (const [id, fill] of this.fills) {
      if (cups.some((c) => c.dish.id === id)) continue;
      fill.group.removeFromParent();
      this.fills.delete(id);
    }
    this.room.cooking?.board.forEach((at, i) => {
      const basket = s.board[i];
      if (basket) place(basket.dish, this.scene, at.x, at.y, at.z, "upright");
    });
    if (s.held?.kind === "rack") {
      s.held.dishes.forEach((d, i) => {
        const [x, y, z] = rackSlot(i);
        place(d, this.heldRack, x, y + rackLift(d.kind), z, "racked");
      });
    }
    for (const [id, m] of this.dishes) {
      if (seen.has(id)) continue;
      m.group.removeFromParent();
      this.dishes.delete(id);
    }
  }

  private syncHands(s: ShiftState, f: Frame, bob: number): void {
    const held = s.held;
    this.heldRack.visible = held?.kind === "rack";
    this.heldRack.position.set(0, -0.3, -0.55);
    this.heldRack.rotation.set(0.35, 0, 0);
    const sway = Math.sin(f.time * 4.5) * 0.01 * f.walking;
    const k = this.handSpread;
    if (held?.kind === "rack") {
      this.gloveL.position.set(-0.27 * k, -0.27 - bob, -0.5);
      this.gloveR.position.set(0.27 * k, -0.27 - bob, -0.5);
      this.gloveL.rotation.set(0, 0.3, 0);
      this.gloveR.rotation.set(0, -0.3, 0);
    } else if (held) {
      this.gloveL.position.set(-0.13 * k, -0.23 - bob, -0.47);
      this.gloveR.position.set(0.13 * k, -0.23 - bob, -0.47);
      this.gloveL.rotation.set(0, 0.6, 0);
      this.gloveR.rotation.set(0, -0.6, 0);
    } else {
      // Resting low in the corners; the right hand swipes on a scrub.
      const since = f.time - f.scrubbedAt;
      const swipe = since < 0.25 ? Math.sin((since / 0.25) * Math.PI) : 0;
      this.gloveL.position.set(-0.22 * k, -0.23 - bob + sway, -0.42);
      this.gloveR.position.set(0.22 * k - swipe * 0.14, -0.23 - bob - sway + swipe * 0.04, -0.42 - swipe * 0.14);
      this.gloveL.rotation.set(0.2, 0.25, 0);
      this.gloveR.rotation.set(0.2, -0.25, swipe * 0.6);
    }
    this.sponge.visible = held === null;
  }

  private syncMachine(s: ShiftState, f: Frame, dt: number): void {
    const status = machineStatus(s);
    const target = status === "washing" ? 0 : 1;
    this.hoodOpen += Math.sign(target - this.hoodOpen) * Math.min(Math.abs(target - this.hoodOpen), dt * 2.5);
    this.room.hood.position.y = this.hoodOpen * HOOD_LIFT;
    this.room.lamp.emissive.setHex(status === "washing" ? 0xf59e0b : status === "done" ? 0x22c55e : 0x3b82f6);
    // Steam while washing, and a last breath when it opens.
    const m = this.room.machineBox;
    this.steam.forEach((p, i) => {
      const life = (f.time * 0.6 + i / this.steam.length) % 1;
      const on = status === "washing" || (status === "done" && s.machine && s.t - s.machine.doneAt < 2);
      const mat = p.material as THREE.MeshBasicMaterial;
      mat.opacity = on ? 0.35 * (1 - life) : Math.max(0, mat.opacity - dt);
      p.position.set(m.min[0] + 0.1 + ((i * 0.37) % 0.66), 1.4 + this.hoodOpen * HOOD_LIFT + life * 0.6, m.min[2] + 0.2 + ((i * 0.23) % 0.4));
      p.scale.setScalar(0.04 + life * 0.08);
    });
  }

  private syncShelf(s: ShiftState): void {
    for (const kind of setupKinds(s.setup)) {
      const stack = this.shelfStacks[kind];
      const n = Math.min(SHELF_MAX, s.shelf[kind] ?? 0);
      while (stack.length < n) {
        const m = makeDish(kind);
        m.setGrime(0);
        this.scene.add(m.group);
        stack.push(m);
      }
      stack.forEach((m, i) => {
        m.group.visible = i < n;
        const spot = this.room.shelfSpot(kind, i);
        m.group.position.copy(spot.pos);
        m.group.rotation.set(spot.flip ? Math.PI : 0, spot.turn, 0);
      });
    }
  }

  private syncTickets(s: ShiftState): void {
    const live = new Set(s.tickets.map((t) => t.id));
    for (const [id, mesh] of this.tickets) {
      if (live.has(id)) continue;
      mesh.removeFromParent();
      (mesh.material as THREE.MeshBasicMaterial).map?.dispose();
      this.tickets.delete(id);
    }
    const rail = this.room.anchors.ticketRail;
    s.tickets.slice(0, 10).forEach((t, i) => {
      let mesh = this.tickets.get(t.id);
      if (!mesh) {
        const dishes = t.build ? describeBuild(t.build) : t.dishes.map((d) => DISHES[d].name).join(" ");
        mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.1, 0.125), new THREE.MeshBasicMaterial({ map: ticketTexture(`#${t.no} ${t.item}`, dishes), side: THREE.DoubleSide }));
        mesh.rotation.y = Math.PI;
        this.scene.add(mesh);
        this.tickets.set(t.id, mesh);
      }
      mesh.position.set(rail.x + 0.85 - i * 0.17, rail.y - 0.065, rail.z - 0.005);
      // Tickets curl as they wait.
      const wait = (s.t - t.placedAt) / s.setup.patience;
      mesh.rotation.x = wait * 0.5;
      (mesh.material as THREE.MeshBasicMaterial).color.setHex(wait > 0.66 ? 0xffb4a8 : 0xffffff);
    });
  }

  private syncHighlight(aim: Aim): void {
    for (const [station, mats] of Object.entries(this.room.highlight) as [Station, THREE.MeshStandardMaterial[]][]) {
      if (station === "griddle" || station === "fryer") {
        for (const m of mats) m.emissive.setHex(aim?.station === station ? 0x1a1a12 : 0x000000);
        continue;
      }
      const on = aim?.station === station && aim.dishId === undefined;
      for (const m of mats) m.emissive.setHex(on ? 0x2a2a1e : 0x000000);
    }
  }

  /** The coffee truck: shots pulling under the group heads, milk on the wand, the cup on the bar, and what's in hand. */
  private syncCafe(s: ShiftState, f: Frame): void {
    if (!this.room.cafe) return;
    s.groups.forEach((shot, i) => {
      this.groupShots[i]!.group.visible = !!shot;
      this.groupFilters[i]!.group.visible = !!shot;
      if (shot) this.groupShots[i]!.set(shot);
      this.groupMarkers[i]!.set(shot ? heatStatus(shot) : null, f.time);
    });
    this.wandPitcher.group.visible = !!s.wand;
    if (s.wand) this.wandPitcher.set(s.wand);
    this.wandMarker.set(s.wand ? heatStatus(s.wand) : null, f.time);
    this.barCup.group.visible = !!s.bar;
    if (s.bar) this.barCup.set(s.bar);
    const held = s.held;
    this.heldFilter.group.visible = held?.kind === "portafilter";
    if (held?.kind === "portafilter") this.heldFilter.set(held.dose);
    this.heldShot.group.visible = held?.kind === "shot";
    if (held?.kind === "shot") this.heldShot.set(held.shot);
    this.heldPitcher.group.visible = held?.kind === "pitcher";
    if (held?.kind === "pitcher") this.heldPitcher.set(held.milk);
    this.heldCoffee.group.visible = held?.kind === "coffee";
    if (held?.kind === "coffee") this.heldCoffee.set(held.cup);
  }

  /** The customers out front: placed and posed from the sim's times. */
  private syncPeople(s: ShiftState, f: Frame): void {
    const waiting = s.customers.filter((c) => c.doneAt === undefined && s.t >= c.orderAt).map((c) => c.no);
    const seen = new Set<number>();
    for (const c of s.customers) {
      let model = this.people.get(c.no);
      if (!model) {
        model = new PersonModel(c.no);
        this.people.set(c.no, model);
        this.scene.add(model.group);
      }
      // Where they stood waiting; someone leaving walks off from there.
      if (c.doneAt === undefined && waiting.includes(c.no)) this.spots.set(c.no, waiting.indexOf(c.no));
      const spot = this.spots.get(c.no) ?? 0;
      model.update(customerPose(c, s.t, spot), f.time, c.no * 1.7);
      seen.add(c.no);
    }
    for (const [no, model] of this.people) {
      if (seen.has(no)) continue;
      model.dispose();
      this.people.delete(no);
      this.spots.delete(no);
    }
  }

  /** The food you can see: patties on the flat-top, fries in the fryer, the order on the board, food in hand, smoke, the bell. */
  private syncCooking(s: ShiftState, f: Frame): void {
    const held = s.held;
    this.heldPatty.mesh.visible = held?.kind === "patty";
    if (held?.kind === "patty") this.heldPatty.set(held.patty);
    this.heldFries.group.visible = held?.kind === "fries";
    if (held?.kind === "fries") this.heldFries.set(held.fries);
    this.heldContents.group.visible = held?.kind === "basket";
    if (held?.kind === "basket") this.heldContents.set(held.basket.contents);
    const cooking = this.room.cooking;
    if (!cooking) return;
    this.griddlePatties.forEach((model, i) => {
      const p = s.griddle[i];
      model.mesh.visible = !!p;
      if (p) model.set(p);
      this.griddleMarkers[i]!.set(p ? pattyStatus(p) : null, f.time);
      // Aimed at: lift it a hair so you can see which one you'll flip.
      model.mesh.position.y = cooking.griddle[i]!.y + 0.009 + (f.aim?.station === "griddle" && f.aim.slot === i ? 0.006 : 0);
    });
    this.fryerFries.forEach((model, i) => {
      const fries = s.fryer[i];
      model.group.visible = !!fries;
      if (fries) model.set(fries);
      this.fryerMarkers[i]!.set(fries ? friesStatus(fries) : null, f.time);
      // Bob in the bubbling oil.
      model.group.position.y = cooking.fryer[i]!.y - 0.01 + Math.sin(f.time * 7 + i) * 0.003;
    });
    this.boardContents.forEach((model, i) => {
      const basket = s.board[i];
      model.group.visible = !!basket;
      if (basket) model.set(basket.contents);
    });
    // Smoke off a patty that's past done on the bottom.
    let puff = 0;
    s.griddle.forEach((p, i) => {
      if (!p || p.sides[0] < 1.25 || puff >= this.smoke.length) return;
      const at = cooking.griddle[i]!;
      const heavy = pattyDoneness(p) === "burnt" ? 1 : (p.sides[0] - 1.25) / 0.75;
      for (let k = 0; k < 3 && puff < this.smoke.length; k++, puff++) {
        const m = this.smoke[puff]!;
        const life = (f.time * 0.8 + k / 3) % 1;
        m.position.set(at.x + Math.sin(k * 2.1 + f.time) * 0.03, at.y + 0.03 + life * 0.35, at.z + Math.cos(k * 1.7) * 0.03);
        m.scale.setScalar(0.02 + life * 0.05);
        (m.material as THREE.MeshBasicMaterial).opacity = 0.45 * heavy * (1 - life);
      }
    });
    for (; puff < this.smoke.length; puff++) (this.smoke[puff]!.material as THREE.MeshBasicMaterial).opacity = 0;
    // The bell dips when it's rung.
    const since = f.time - f.rungAt;
    cooking.bell.scale.y = since < 0.2 ? 1 - Math.sin((since / 0.2) * Math.PI) * 0.25 : 1;
  }

  private syncSparkles(s: ShiftState, f: Frame): void {
    // Glint where the newest dish landed in the rack.
    if (s.rack.length > this.lastRackCount) {
      const [x, y, z] = rackSlot(s.rack.length - 1);
      this.sparkleAt.set(x, y + rackLift(s.rack.at(-1)!.kind) + 0.06, z).add(this.room.anchors.rack);
    }
    this.lastRackCount = s.rack.length;
    const since = f.time - f.cleanAt;
    this.sparkles.forEach((m, i) => {
      const mat = m.material as THREE.MeshBasicMaterial;
      if (since > 0.7) {
        mat.opacity = 0;
        return;
      }
      const k = since / 0.7;
      const a = i * 1.05 + f.cleanAt * 3;
      m.position.set(this.sparkleAt.x + Math.cos(a) * 0.09 * (0.4 + k), this.sparkleAt.y + 0.04 * i * k + Math.sin(a * 2) * 0.03, this.sparkleAt.z + Math.sin(a) * 0.09 * (0.4 + k));
      m.quaternion.copy(this.camera.quaternion);
      m.rotateZ(k * 2 + i);
      m.scale.setScalar(0.022 * Math.sin(Math.PI * Math.min(1, k * 1.3 + (i % 3) * 0.08)));
      mat.opacity = 1 - k;
    });
  }

  private syncBubbles(f: Frame, dt: number): void {
    const since = f.time - f.scrubbedAt;
    const a = this.room.anchors.sink;
    this.bubbles.forEach((b, i) => {
      const mat = b.material as THREE.MeshStandardMaterial;
      if (since < 0.6) {
        const k = since / 0.6;
        const ang = i * 0.52 + f.scrubbedAt;
        b.position.set(a.x + Math.cos(ang) * (0.05 + k * 0.12), a.y + 0.03 + k * (0.15 + (i % 3) * 0.05), a.z + Math.sin(ang) * (0.05 + k * 0.1));
        b.scale.setScalar(0.01 + (i % 4) * 0.004);
        mat.opacity = 0.8 * (1 - k);
      } else mat.opacity = Math.max(0, mat.opacity - dt * 3);
    });
  }
}
